import Sidebar from "../components/sidebar";
import { useEffect, useMemo, useState } from "react";
import AdminUserModel from "../domain/models/adminUser.model";
import UserService from "../infraestructure/services/user.service";

const UserView = () => {
  const [users, setUsers] = useState<AdminUserModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setError("");

      try {
        const userService = new UserService();
        setUsers(await userService.getAdminUsers());
      } catch (err) {
        console.error("Error fetching admin users:", err);
        setError("No se pudo cargar la lista de usuarios");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return users;
    }

    return users.filter((user) => {
      const status = [
        user.isActive ? "activo" : "inactivo",
        user.isLogin ? "conectado" : "desconectado",
      ].join(" ");

      return [
        user.email,
        user.telefono || "",
        status,
        formatDate(user.fechaRegistro),
        formatDateTime(user.lastActivity),
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [searchTerm, users]);

  const totals = useMemo(
    () => ({
      users: users.length,
      active: users.filter((user) => user.isActive).length,
      loggedIn: users.filter((user) => user.isLogin).length,
      children: users.reduce((total, user) => total + Number(user.childrenCount || 0), 0),
    }),
    [users]
  );

  const renderStatusBadge = (isEnabled: boolean, activeText: string, inactiveText: string) => (
    <span className={`badge ${isEnabled ? "bg-success" : "bg-secondary"}`}>
      {isEnabled ? activeText : inactiveText}
    </span>
  );

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={7} className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only"></span>
            </div>
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td colSpan={7} className="text-center text-danger">
            {error}
          </td>
        </tr>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="text-center text-muted">
            No hay usuarios disponibles
          </td>
        </tr>
      );
    }

    return filteredUsers.map((user) => (
      <tr key={user.id}>
        <td>{user.email}</td>
        <td>{user.telefono || "Sin teléfono"}</td>
        <td>{formatDate(user.fechaRegistro)}</td>
        <td>{formatDateTime(user.lastActivity)}</td>
        <td>{user.childrenCount}</td>
        <td>{renderStatusBadge(user.isActive, "Activo", "Inactivo")}</td>
        <td>{renderStatusBadge(user.isLogin, "Conectado", "Desconectado")}</td>
      </tr>
    ));
  };

  return (
    <div className="background-colored">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-2 justify-content-center sidebar">
          <Sidebar />
        </div>
        <div className="col-12 col-sm-9 justify-content-center datatable-container ml">
          <div className="header-container">
            <div>
              <span id="customized" className="header-label">
                Usuarios{" "}
              </span>
            </div>
          </div>

          <div className="user-summary mt-4">
            <div className="user-summary-item">
              <span className="user-summary-label">Usuarios</span>
              <strong>{totals.users}</strong>
            </div>
            <div className="user-summary-item">
              <span className="user-summary-label">Activos</span>
              <strong>{totals.active}</strong>
            </div>
            <div className="user-summary-item">
              <span className="user-summary-label">Conectados</span>
              <strong>{totals.loggedIn}</strong>
            </div>
            <div className="user-summary-item">
              <span className="user-summary-label">Niños</span>
              <strong>{totals.children}</strong>
            </div>
          </div>

          <div className="mt-4">
            <input
              className="form-control"
              placeholder="Buscar por correo, teléfono o estado"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="mt-4 users-table-container">
            <table className="table table-striped align-middle users-table">
              <thead>
                <tr>
                  <th scope="col" className="th users-email-cell">
                    Correo
                  </th>
                  <th scope="col" className="th users-phone-cell">
                    Teléfono
                  </th>
                  <th scope="col" className="th users-date-cell">
                    Registro
                  </th>
                  <th scope="col" className="th users-date-cell">
                    Última actividad
                  </th>
                  <th scope="col" className="th users-count-cell">
                    Niños
                  </th>
                  <th scope="col" className="th users-status-cell">
                    Cuenta
                  </th>
                  <th scope="col" className="th users-status-cell">
                    Sesión
                  </th>
                </tr>
              </thead>
              <tbody>{renderTableContent()}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "No disponible";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "No disponible";
  }

  return new Intl.DateTimeFormat("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "No disponible";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "No disponible";
  }

  return new Intl.DateTimeFormat("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export default UserView;
